import { useState, useRef, useEffect, useCallback } from 'react';
import { qrCheckin, idCheckin } from '../../services/doctorService';

// ── Audio feedback ────────────────────────────────────────────────────────────
function playSound(type) {
  try {
    const ctx  = new (window.AudioContext || window.webkitAudioContext)();
    const osc  = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    if (type === 'success') {
      osc.type = 'sine';
      osc.frequency.setValueAtTime(520, ctx.currentTime);
      osc.frequency.setValueAtTime(780, ctx.currentTime + 0.12);
      gain.gain.setValueAtTime(0.4, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
      osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.4);
    } else if (type === 'error') {
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(300, ctx.currentTime);
      osc.frequency.setValueAtTime(150, ctx.currentTime + 0.2);
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
      osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.35);
    } else if (type === 'duplicate') {
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(440, ctx.currentTime);
      gain.gain.setValueAtTime(0.2, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
      osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.25);
    }
  } catch {}
}

export default function ScannerInput({ onCheckinSuccess }) {
  const [inputValue, setInputValue] = useState('');
  const [status, setStatus]         = useState('idle'); // idle|scanning|loading|success|error|duplicate
  const [patientData, setPatientData] = useState(null);
  const [errorMsg, setErrorMsg]     = useState('');
  const [scanCount, setScanCount]   = useState(0);

  const inputRef          = useRef(null);
  const lastKeyTime       = useRef(Date.now());
  const keystrokeTimes    = useRef([]);
  const debounceTimer     = useRef(null);
  const lastScannedToken  = useRef('');
  const lastScanTime      = useRef(0);
  const isProcessing      = useRef(false);
  // FIX: track if current input came from Enter key to avoid double-processing
  const processedByEnter  = useRef(false);

  const focusInput = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  // FIX: only re-focus on click if the click was NOT on an interactive element inside this component
  useEffect(() => {
    focusInput();
    const handleClick = (e) => {
      const tag = e.target.tagName;
      // Don't steal focus from buttons, inputs, selects
      if (tag === 'BUTTON' || tag === 'INPUT' || tag === 'SELECT' || tag === 'A') return;
      setTimeout(focusInput, 100);
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [focusInput]);

  const resetToIdle = useCallback((delay = 2500) => {
    setTimeout(() => {
      setStatus('idle');
      setInputValue('');
      setPatientData(null);
      setErrorMsg('');
      keystrokeTimes.current  = [];
      isProcessing.current    = false;
      processedByEnter.current = false;
      focusInput();
    }, delay);
  }, [focusInput]);

  const processScan = useCallback(async (qrToken) => {
    const trimmed = qrToken.trim();
    if (!trimmed || trimmed.length < 5) return;
    if (isProcessing.current) return;

    const now = Date.now();

    // Duplicate scan protection (5 seconds)
    if (trimmed === lastScannedToken.current && now - lastScanTime.current < 5000) {
      playSound('duplicate');
      setStatus('duplicate');
      setErrorMsg('Already scanned — please wait 5 seconds before scanning again.');
      resetToIdle(2000);
      return;
    }

    isProcessing.current     = true;
    lastScannedToken.current = trimmed;
    lastScanTime.current     = now;
    setStatus('loading');
    setPatientData(null);
    setErrorMsg('');

    try {
      // Determine: JWT QR token (3 dot-separated parts) or plain booking ID
      const isJWT = trimmed.split('.').length === 3 && !trimmed.includes(' ');
      let res;
      if (isJWT) {
        res = await qrCheckin(trimmed);
      } else {
        // Could be a booking ID or JSON-encoded QR
        let id = trimmed;
        try { const p = JSON.parse(trimmed); id = p.bookingId || trimmed; } catch {}
        res = await idCheckin(id);
      }
      if (res.success) {
        playSound('success');
        setStatus('success');
        setPatientData({ patient: res.patient, appointment: res.appointment });
        setScanCount(p => p + 1);
        onCheckinSuccess?.({ patient: res.patient, appointment: res.appointment });
        // Don't auto-reset — show Done button so staff can confirm
      } else {
        throw new Error(res.error || 'Check-in failed');
      }
    } catch (err) {
      playSound('error');
      setStatus('error');
      const reason = err.response?.data?.error
        || err.response?.data?.reason
        || err.message
        || 'Invalid or expired QR code';
      setErrorMsg(reason);
      resetToIdle(3000);
    }
  }, [onCheckinSuccess, resetToIdle]);

  const handleChange = useCallback((e) => {
    const value = e.target.value;
    setInputValue(value);
    setStatus('scanning');

    const now = Date.now();
    const gap = now - lastKeyTime.current;
    lastKeyTime.current = now;
    keystrokeTimes.current.push(gap);
    if (keystrokeTimes.current.length > 12) keystrokeTimes.current.shift();

    // FIX: clear debounce — Enter key handler takes priority
    clearTimeout(debounceTimer.current);
    processedByEnter.current = false;

    // Debounce auto-submit (scanner sends data fast then stops)
    debounceTimer.current = setTimeout(() => {
      // FIX: don't double-process if Enter already handled it
      if (processedByEnter.current) return;
      if (value.trim().length >= 5) processScan(value);
      else if (value.trim().length === 0) setStatus('idle');
    }, 350);
  }, [processScan]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      clearTimeout(debounceTimer.current);
      if (inputValue.trim().length >= 5) {
        processedByEnter.current = true; // prevent debounce from double-firing
        processScan(inputValue);
      }
    }
    if (e.key === 'Escape') {
      clearTimeout(debounceTimer.current);
      setInputValue('');
      setStatus('idle');
      setPatientData(null);
      setErrorMsg('');
      keystrokeTimes.current  = [];
      isProcessing.current    = false;
      processedByEnter.current = false;
    }
  }, [inputValue, processScan]);

  // Scanner detection: 4+ consecutive keystrokes each < 60ms apart
  const isFromScanner = keystrokeTimes.current.length >= 4 &&
    keystrokeTimes.current.slice(-4).every(g => g < 60);

  const borderColor = {
    idle:      '#E2E8F0',
    scanning:  isFromScanner ? '#0D9488' : '#F59E0B',
    loading:   '#3B82F6',
    success:   '#10B981',
    error:     '#EF4444',
    duplicate: '#F59E0B',
  }[status] || '#E2E8F0';

  const statusLabel = {
    idle:      'Ready to Scan',
    scanning:  isFromScanner ? '⚡ Scanner Detected...' : '⌨️ Manual Input...',
    loading:   'Verifying...',
    success:   '✅ Checked In!',
    error:     '❌ Failed',
    duplicate: '⚠️ Duplicate',
  }[status];

  return (
    <div style={S.wrapper}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:14, gap:8, flexWrap:'wrap' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, minWidth:0 }}>
          <i className="fa-solid fa-qrcode" style={{ fontSize:20, color:'#0D9488', flexShrink:0 }} />
          <div>
            <div style={{ fontSize:14, fontWeight:700, color:'#0F172A', lineHeight:1.2 }}>Physical QR Scanner</div>
            <div style={{ fontSize:11, color:'#94A3B8', marginTop:1 }}>USB · Bluetooth · Any device</div>
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:20, padding:'3px 10px', fontSize:11, color:'#166534', fontWeight:600, flexShrink:0, whiteSpace:'nowrap' }}>
          <i className="fa-solid fa-circle-check" style={{ color:'#10B981', marginRight:5, fontSize:10 }} />
          {scanCount} scanned
        </div>
      </div>

      {/* Scanner input area */}
      <div style={S.scanArea}>
        {/* Status indicator */}
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12 }}>
          <div style={{
            width:9, height:9, borderRadius:'50%',
            background: borderColor,
            boxShadow: status !== 'idle' ? `0 0 0 3px ${borderColor}30` : 'none',
            transition: 'all 0.3s',
            flexShrink:0,
          }} />
          <span style={{ fontSize:12, fontWeight:600, color: borderColor, transition:'color 0.3s' }}>
            {statusLabel}
          </span>
        </div>

        {/* Input box */}
        <div style={{
          display:'flex', alignItems:'center',
          background:'#FFF', border:`2px solid ${borderColor}`,
          borderRadius:10, padding:'0 14px',
          transition:'all 0.25s', minHeight:52,
          boxShadow:`0 0 0 3px ${borderColor}18`,
        }}>
          <i className="fa-solid fa-barcode" style={{ fontSize:16, color:'#94A3B8', marginRight:10, flexShrink:0 }} />
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder="Scan QR here..."
            autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false}
            readOnly={status === 'loading'}
            style={{ flex:1, border:'none', outline:'none', fontSize:14, color:'#0F172A', background:'transparent', padding:'14px 0' }}
          />
          {status === 'loading' && (
            <div style={{ width:18, height:18, border:'2px solid #E2E8F0', borderTopColor:'#0D9488', borderRadius:'50%', animation:'spin 0.7s linear infinite', flexShrink:0 }} />
          )}
          {inputValue && status !== 'loading' && (
            <button
              onMouseDown={e => { e.preventDefault(); setInputValue(''); setStatus('idle'); focusInput(); }}
              style={{ background:'none', border:'none', cursor:'pointer', color:'#94A3B8', fontSize:13, padding:'2px 6px', flexShrink:0 }}>
              <i className="fa-solid fa-times" />
            </button>
          )}
        </div>

        <div style={{ fontSize:11, color:'#94A3B8', marginTop:8, display:'flex', alignItems:'center', flexWrap:'wrap', gap:3 }}>
          <i className="fa-solid fa-circle-info" style={{ marginRight:3 }} />
          Auto-detects USB scanner · Press <kbd style={S.kbd}>Enter</kbd> manually · <kbd style={S.kbd}>Esc</kbd> clear
        </div>
      </div>

      {/* Success card */}
      {status === 'success' && patientData && (
        <div style={S.successCard}>
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:12 }}>
            <div style={{ width:40, height:40, borderRadius:'50%', background:'#10B981', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, color:'#FFF', flexShrink:0 }}>
              <i className="fa-solid fa-circle-check" />
            </div>
            <div>
              <div style={{ fontSize:15, fontWeight:700, color:'#166534' }}>Checked In!</div>
              <div style={{ fontSize:12, color:'#16A34A', marginTop:1 }}>Token #{patientData.appointment?.tokenNumber}</div>
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
            {[
              { icon:'fa-user',       label:'Name',         val: patientData.patient?.name },
              { icon:'fa-venus-mars', label:'Gender / Age', val: `${patientData.patient?.gender || '—'} · ${patientData.patient?.age || '—'}y` },
              { icon:'fa-clock',      label:'Slot',         val: patientData.appointment?.slotTime },
              { icon:'fa-id-badge',   label:'Booking ID',   val: patientData.appointment?.bookingId, small: true },
            ].map(f => (
              <div key={f.label} style={{ background:'#FFF', borderRadius:8, padding:'8px 12px' }}>
                <div style={{ fontSize:10, color:'#64748B', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.4px', marginBottom:3, display:'flex', alignItems:'center', gap:4 }}>
                  <i className={`fa-solid ${f.icon}`} style={{ color:'#0D9488' }} /> {f.label}
                </div>
                <div style={{ fontSize: f.small ? 11 : 13, fontWeight:600, color:'#0F172A', wordBreak:'break-all' }}>{f.val || '—'}</div>
              </div>
            ))}
          </div>
          {/* Done + Next Patient buttons */}
          <div style={{ display:'flex', gap:8, marginTop:12 }}>
            <button
              onClick={() => { resetToIdle(0); onCheckinSuccess?.({ done: true, patient: patientData.patient, appointment: patientData.appointment }); }}
              style={{ flex:1, padding:'10px 0', background:'#10B981', color:'#FFF', border:'none', borderRadius:10, fontWeight:700, fontSize:13, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
              <i className="fa-solid fa-circle-check" /> Done
            </button>
            <button
              onClick={() => { setStatus('idle'); setInputValue(''); setPatientData(null); setErrorMsg(''); isProcessing.current = false; focusInput(); }}
              style={{ flex:1, padding:'10px 0', background:'#FFF', color:'#0D9488', border:'1.5px solid #0D9488', borderRadius:10, fontWeight:600, fontSize:13, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
              <i className="fa-solid fa-qrcode" /> Next Patient
            </button>
          </div>
        </div>
      )}

      {/* Error / duplicate card */}
      {(status === 'error' || status === 'duplicate') && (
        <div style={{
          display:'flex', alignItems:'flex-start',
          border:`1.5px solid ${status === 'duplicate' ? '#F59E0B' : '#EF4444'}`,
          background: status === 'duplicate' ? '#FFFBEB' : '#FFF5F5',
          borderRadius:10, padding:'12px 16px', marginBottom:12,
        }}>
          <i className={`fa-solid ${status === 'duplicate' ? 'fa-triangle-exclamation' : 'fa-circle-xmark'}`}
            style={{ fontSize:20, color: status === 'duplicate' ? '#F59E0B' : '#EF4444', marginRight:10, marginTop:1, flexShrink:0 }} />
          <div>
            <div style={{ fontWeight:600, color:'#0F172A', marginBottom:2, fontSize:14 }}>
              {status === 'duplicate' ? 'Duplicate Scan' : 'Check-In Failed'}
            </div>
            <div style={{ fontSize:12, color:'#64748B' }}>{errorMsg}</div>
          </div>
        </div>
      )}

      {/* Device chips */}
      <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginTop:4 }}>
        {[
          { icon:'fa-usb',           label:'USB' },
          { icon:'fa-bluetooth', brand:true, label:'Bluetooth' },
          { icon:'fa-mobile-screen', label:'Mobile' },
          { icon:'fa-desktop',       label:'Desktop' },
        ].map(d => (
          <div key={d.label} style={S.chip}>
            <i className={`fa-solid ${d.icon}`} style={{ marginRight:5 }} /> {d.label}
          </div>
        ))}
      </div>
    </div>
  );
}

const S = {
  wrapper: {
    fontFamily: 'system-ui, -apple-system, sans-serif',
    background: '#FFF', borderRadius:12,
    border: '1px solid #E2E8F0', padding:16,
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    width: '100%', boxSizing: 'border-box',
  },
  scanArea: { background:'#F8FAFC', borderRadius:10, padding:16, marginBottom:14 },
  successCard: {
    background:'#F0FDF4', border:'1.5px solid #86EFAC',
    borderRadius:10, padding:'14px 16px', marginBottom:14,
  },
  kbd: {
    background:'#F1F5F9', border:'1px solid #CBD5E1',
    borderRadius:4, padding:'1px 5px', fontSize:10,
    fontFamily:'monospace', color:'#475569', margin:'0 2px',
  },
  chip: {
    display:'flex', alignItems:'center', background:'#F1F5F9',
    borderRadius:20, padding:'3px 10px', fontSize:11, color:'#64748B',
  },
};
